import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Tanitim sayfasindaki demo talep formu.
 *
 * Onceden bu form yalnizca preventDefault() yapiyordu: ziyaretci "Talep
 * Gonder"e basiyor, hicbir sey gonderilmiyordu. Artik /api/DemoRequest'e
 * gidiyor ve talep e-posta olarak iletiliyor.
 *
 * "website" alani bir BOT TUZAGI: ekranda gizli, insan doldurmaz. Sunucu
 * doluysa istegi sessizce yok sayar.
 */

interface FormState {
    fullName: string;
    email: string;
    companyName: string;
    position: string;
    phone: string;
    allowEmailContact: boolean;
    website: string;
}

const EMPTY: FormState = {
    fullName: '', email: '', companyName: '', position: '', phone: '',
    allowEmailContact: false, website: '',
};

const inputClass =
    'w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 ' +
    'focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition';

const DemoRequestForm: React.FC = () => {
    const [form, setForm] = useState<FormState>(EMPTY);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
        setForm(f => ({ ...f, [key]: value }));

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSending(true);
        try {
            const res = await fetch('/api/DemoRequest', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });

            if (!res.ok) {
                // Sunucunun mesajini gostermeye calis; yoksa genel bir metin.
                let message = 'Talebiniz gönderilemedi. Lütfen daha sonra tekrar deneyin.';
                try {
                    const body = await res.json();
                    message = body?.message ?? body?.title ?? message;
                } catch { /* govde okunamadi, genel mesaj kalsin */ }
                setError(message);
                return;
            }

            setDone(true);
            setForm(EMPTY);
        } catch {
            setError('Bağlantı kurulamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.');
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <div className="p-8 rounded-2xl bg-white border border-gray-100 shadow-xl shadow-gray-100/50 text-center">
                <div className="text-4xl mb-3">✅</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Talebiniz alındı</h3>
                <p className="text-gray-500">En kısa sürede sizinle iletişime geçeceğiz.</p>
                <button
                    type="button"
                    onClick={() => setDone(false)}
                    className="mt-6 text-indigo-600 hover:text-indigo-700 font-medium"
                >
                    Yeni talep gönder
                </button>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            className="p-8 rounded-2xl bg-white border border-gray-100 shadow-xl shadow-gray-100/50 space-y-5"
        >
            <h3 className="text-xl font-semibold text-gray-900">Demo Talebi</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="block text-sm text-gray-600 mb-1.5">
                        <span className="text-red-500">*</span> Ad Soyad
                    </label>
                    <input
                        required maxLength={150} value={form.fullName}
                        onChange={e => set('fullName', e.target.value)}
                        className={inputClass} placeholder="Adınızı ve soyadınızı girin"
                    />
                </div>
                <div>
                    <label className="block text-sm text-gray-600 mb-1.5">
                        <span className="text-red-500">*</span> İş E-posta Adresi
                    </label>
                    <input
                        required type="email" maxLength={200} value={form.email}
                        onChange={e => set('email', e.target.value)}
                        className={inputClass} placeholder="Kurumsal e-posta adresinizi girin"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="block text-sm text-gray-600 mb-1.5">
                        <span className="text-red-500">*</span> Şirket Adı
                    </label>
                    <input
                        required maxLength={200} value={form.companyName}
                        onChange={e => set('companyName', e.target.value)}
                        className={inputClass} placeholder="Şirket adınızı girin"
                    />
                </div>
                <div>
                    <label className="block text-sm text-gray-600 mb-1.5">
                        <span className="text-red-500">*</span> Pozisyon
                    </label>
                    <input
                        required maxLength={150} value={form.position}
                        onChange={e => set('position', e.target.value)}
                        className={inputClass} placeholder="Pozisyonunuzu girin"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm text-gray-600 mb-1.5">
                    <span className="text-red-500">*</span> Telefon Numarası
                </label>
                <input
                    required type="tel" maxLength={40} value={form.phone}
                    onChange={e => set('phone', e.target.value)}
                    className={inputClass} placeholder="Telefon numaranızı girin"
                />
            </div>

            {/* Bot tuzagi: ekranda gorunmez, ekran okuyuculardan da gizli. */}
            <div className="hidden" aria-hidden="true">
                <label>
                    Web sitesi
                    <input
                        tabIndex={-1} autoComplete="off" value={form.website}
                        onChange={e => set('website', e.target.value)}
                    />
                </label>
            </div>

            <p className="text-sm text-gray-500 leading-relaxed">
                Formu göndererek{' '}
                <Link to="/kullanim-kosullari" target="_blank" className="font-semibold text-gray-700 hover:text-indigo-600 underline">
                    Kullanım Koşulları
                </Link>{' '}
                ve{' '}
                <Link to="/kvkk" target="_blank" className="font-semibold text-gray-700 hover:text-indigo-600 underline">
                    Kişisel Verilerin İşlenmesine İlişkin Aydınlatma Metni
                </Link>
                ’ni okuduğunuzu ve anladığınızı beyan etmiş olursunuz.
            </p>

            <label className="flex items-center gap-3 cursor-pointer">
                <input
                    type="checkbox" checked={form.allowEmailContact}
                    onChange={e => set('allowEmailContact', e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-gray-900 font-medium">E-posta ile bilgilendirilmek istiyorum</span>
            </label>

            {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
                    {error}
                </div>
            )}

            <button
                type="submit"
                disabled={sending}
                className="w-full py-4 rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 text-white font-semibold text-lg hover:shadow-xl hover:shadow-indigo-500/25 transition-all hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
            >
                {sending ? 'Gönderiliyor…' : 'Talep Gönder →'}
            </button>
        </form>
    );
};

export default DemoRequestForm;
